import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { ArrowUpRight, Check, Search, Shield, MapPin, Bell, Star, AlertTriangle, Sparkles, FileText, ScrollText, ShieldCheck, Linkedin } from "lucide-react";
import { TYPEFORM, LOGO_SRC , withUTM } from "../data/landing";
const T = Object.fromEntries(Object.entries(TYPEFORM).map(([k, v]) => [k, withUTM(v, "for-licensors")]));

const A = {
  bgBlue: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/bvyosrzq_BG1.png",
  bgPink: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/4lkxeo9g_BG2.png",
  bgPurple: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/raqsjlz5_BG3.png",
  bgOrange: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/pphq1t1h_BG4.png",
  strangerPhone: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/tqciwod0_egeez-screen.png",
  ticketPhone: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/tqciwod0_egeez-screen.png",
  insightsBg: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/n8onuyxg_image.png",
  netflix: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/y7d9eztc_netflix.jpg",
  heroLicensorImg: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/r7rokvkl_higher%20rez.png",
  premiereLogo: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/gs9xwyp4_Premimere%20Drop%20logo.png",
  premiereLeaderboard: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/m7ixlnt4_image.png",
  geoShield: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/0zah2x2y_image.png",
  cannotShip: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/dzwk3apu_image.png",
};

const RESOURCES_URL = "https://help.egeez.com/en/collections/9-for-licensors";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

function Eyebrow({ children, color = "#0041E6" }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase"
      style={{ color }}
    >
      <span className="inline-block w-6 h-px" style={{ background: color }} />
      {children}
    </div>
  );
}

/* ---------- Visual mocks (CSS only, no external images) ---------- */

// eInsights tablet dashboard (used in "Big Data for Big IP Owners" + Revenue section)
function InsightsTablet() {
  return (
    <div className="relative w-full max-w-[540px] aspect-[5/3.4] bg-black rounded-[28px] p-2 shadow-2xl">
      <div className="w-full h-full bg-white rounded-[20px] overflow-hidden flex">
        <div className="w-[110px] bg-[#0041E6] text-white text-[10px] font-semibold p-3 flex flex-col gap-2.5">
          <img src={LOGO_SRC} alt="" className="h-6 w-auto brightness-0 invert" />
          <div className="mt-2 space-y-2">
            <div>Dashboard</div>
            <div>Product List</div>
            <div>Orders</div>
            <div className="bg-white text-[#0041E6] -mx-3 px-3 py-1 font-bold rounded-r-full">
              Insights
            </div>
            <div>Assets</div>
            <div>Account</div>
          </div>
        </div>
        <div className="flex-1 p-3 text-black">
          <div className="w-full h-6 rounded-full bg-[#F1F1F4] flex items-center px-3 text-[8px] text-black/50 gap-2">
            <Search size={9} /> Search Products...
          </div>
          <div className="mt-2 flex items-center gap-1">
            <span
              className="font-black italic text-[#0041E6]"
              style={{ fontFamily: "Inter", fontSize: 16 }}
            >
              e
            </span>
            <span
              className="font-black text-black"
              style={{ fontFamily: "Inter", fontSize: 13 }}
            >
              Insights
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="rounded-lg border border-black/10 p-2">
              <div className="text-[8px] text-black/50">Lifetime Earnings</div>
              <div className="font-black text-base">$ 10K</div>
              <div className="text-[7px] text-black/40">$ 10,124.73 CAD</div>
            </div>
            <div className="rounded-lg border border-black/10 p-2">
              <div className="text-[8px] text-black/50">Monthly Earnings 2024</div>
              <div className="flex items-end gap-0.5 h-7 mt-1">
                {[40, 55, 35, 60, 45, 70, 50, 75, 40, 65, 55].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 bg-[#0041E6] rounded-sm"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="rounded-lg p-2 text-white" style={{ background: "#0041E6" }}>
              <div className="text-[10px] font-black">DISNEY</div>
              <div className="text-[7px] opacity-80">Total Earnings</div>
              <div className="font-black text-base text-[#FFD400]">$ 5K</div>
              <div className="text-[7px] opacity-80 mt-1">+10% vs Last Month</div>
              <div className="mt-1 inline-block bg-white/20 px-2 py-0.5 rounded-full text-[7px]">
                Send Report
              </div>
            </div>
            <div className="rounded-lg p-2 text-white" style={{ background: "#E63946" }}>
              <div className="text-[10px] font-black">WARNER</div>
              <div className="text-[7px] opacity-80">Total Earnings</div>
              <div className="font-black text-base text-[#FFD400]">$ 6K</div>
              <div className="text-[7px] opacity-80 mt-1">+10% vs Last Month</div>
              <div className="mt-1 inline-block bg-white/20 px-2 py-0.5 rounded-full text-[7px]">
                Send Report
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Product list tablet for "Onboard your OWN Merchandise"
function ProductListTablet() {
  return (
    <div className="relative w-full max-w-[540px] aspect-[16/11] bg-black rounded-[24px] p-2 shadow-2xl">
      <div className="w-full h-full bg-white rounded-[16px] overflow-hidden flex">
        <div className="w-[100px] bg-[#0041E6] text-white text-[9px] font-semibold p-3 flex flex-col gap-2">
          <img src={LOGO_SRC} alt="" className="h-5 w-auto brightness-0 invert" />
          <div className="mt-2 space-y-1.5">
            <div>Dashboard</div>
            <div className="bg-white text-[#0041E6] -mx-3 px-3 py-1 font-bold rounded-r-full">
              Product List
            </div>
            <div>Orders</div>
            <div>Insights</div>
            <div>Assets</div>
            <div>Account</div>
          </div>
        </div>
        <div className="flex-1 p-3 text-black overflow-hidden">
          <div className="w-full h-6 rounded-full bg-[#F1F1F4] flex items-center px-3 text-[7px] text-black/50 gap-2">
            <Search size={8} /> Search Products...
          </div>
          <div className="mt-2 font-black text-[#0041E6] text-sm">Product List</div>
          <div className="text-[7px] text-black/50">
            Organize and manage your inventory faster than ever
          </div>
          <div className="flex gap-2 mt-2 text-[8px] border-b border-black/10 pb-1">
            <span className="font-bold border-b-2 border-[#0041E6] pb-1">All products</span>
            <span className="text-black/50">Online [4]</span>
            <span className="text-black/50">Offline [5]</span>
            <span className="text-black/50">Draft [3]</span>
          </div>
          {[
            { name: "Adidas Originals × Disney Goofy", price: "$100.00", payout: "$95.00", flag: "#FFD400" },
            { name: "Adidas Originals × Disney Goofy", price: "$100.00", payout: "$500.00", flag: "#0041E6" },
            { name: "Adidas Originals × Disney Goofy", price: "$100.00", payout: "$95.00", flag: "#1FB8DA" },
          ].map((p, i) => (
            <div
              key={i}
              className="flex items-center gap-2 py-1.5 text-[8px] border-b border-black/5"
            >
              <input type="checkbox" className="w-2.5 h-2.5" readOnly />
              <div className="w-6 h-6 rounded bg-stone-200 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-bold leading-tight">{p.name}</div>
                <div className="text-black/50">SKU: 123-456</div>
              </div>
              <span>{p.price}</span>
              <span className="font-bold">{p.payout}</span>
              <span
                className="w-1.5 h-3 rounded-sm"
                style={{ background: p.flag }}
              />
            </div>
          ))}
          <div className="mt-2 inline-block bg-[#FFD400] text-black text-[7px] font-black px-2 py-0.5 rounded-full">
            DIRECTOR'S CUT
          </div>
        </div>
      </div>
    </div>
  );
}

// Director's chair illustration (CSS) — "Help Licensees" section
function DirectorChair() {
  return (
    <div className="relative w-[280px] md:w-[340px] h-[320px] md:h-[400px] mx-auto">
      {/* legs */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-end gap-32 md:gap-40">
        <div className="w-3 h-48 md:h-60 bg-gradient-to-b from-amber-700 to-amber-900 rounded-sm rotate-[8deg] origin-top" />
        <div className="w-3 h-48 md:h-60 bg-gradient-to-b from-amber-700 to-amber-900 rounded-sm -rotate-[8deg] origin-top" />
      </div>
      {/* seat */}
      <div className="absolute top-[55%] left-1/2 -translate-x-1/2 w-56 md:w-64 h-10 md:h-12 bg-[#0041E6] rounded-sm shadow-lg" />
      {/* back */}
      <div className="absolute top-[18%] left-1/2 -translate-x-1/2 w-56 md:w-64 h-20 md:h-24 bg-[#0041E6] rounded-sm shadow-2xl flex items-center justify-center">
        <span
          className="text-white font-black italic"
          style={{ fontFamily: "Inter", fontSize: "clamp(28px, 4vw, 40px)" }}
        >
          eGeez
        </span>
      </div>
      {/* arm rests */}
      <div className="absolute top-[44%] left-[8%] w-12 h-2 bg-amber-800 rounded-full rotate-[-6deg]" />
      <div className="absolute top-[44%] right-[8%] w-12 h-2 bg-amber-800 rounded-full rotate-[6deg]" />
    </div>
  );
}

// Animated counter — counts up from 0 to target value when in view
function AnimatedCounter({ value, suffix = "", duration = 1.6 }) {
  const [display, setDisplay] = useState(0);
  const [seen, setSeen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !seen) {
          setSeen(true);
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  useEffect(() => {
    if (!seen) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, value, duration]);
  return (
    <span ref={ref}>
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

// 4-up data card for intelligence section — glowing tile with counter + glow
function DataCard({ card, delay = 0 }) {
  const numeric = typeof card.v === "number";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -4 }}
      className="relative rounded-2xl p-4 md:p-5 bg-white/[0.04] backdrop-blur-sm border border-white/10 overflow-hidden group"
      style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04)` }}
      data-testid="intelligence-data-card"
    >
      {/* glow blob */}
      <div
        className="absolute -inset-6 opacity-30 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${card.color}55 0%, transparent 60%)`,
        }}
        aria-hidden
      />
      <div className="relative">
        <div
          className="text-white leading-none"
          style={{
            fontFamily: '"Inter", system-ui, sans-serif',
            fontSize: "clamp(26px, 3vw, 36px)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
          }}
        >
          {numeric ? (
            <AnimatedCounter value={card.v} suffix={card.suffix || ""} />
          ) : (
            <span>
              {card.v}
              {card.suffix && (
                <span className="text-white/55">{card.suffix}</span>
              )}
            </span>
          )}
        </div>
        <div
          className="mt-2 text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/55"
          style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 600 }}
        >
          {card.l}
        </div>
        {/* live ping indicator on numeric cards */}
        {numeric && (
          <span className="absolute top-0 right-0 flex items-center gap-1 text-[9px] tracking-[0.2em] text-white/45">
            <span className="relative flex w-1.5 h-1.5">
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                style={{ background: card.color }}
              />
              <span
                className="relative inline-flex rounded-full h-1.5 w-1.5"
                style={{ background: card.color }}
              />
            </span>
          </span>
        )}
      </div>
    </motion.div>
  );
}

// Animated world map — 28 territory dots that randomly light up
function IntelligenceWorldMap() {
  // Manually scattered dot coordinates roughly mimicking continents on a 100x55 viewBox
  const DOTS = [
    [12, 18], [16, 14], [20, 17], [14, 22], [18, 24], [22, 20], [16, 28], [20, 32],
    [42, 14], [46, 12], [50, 16], [54, 13], [44, 20], [48, 22], [52, 18], [46, 26],
    [56, 26], [60, 28], [58, 32], [62, 22], [70, 18], [74, 22], [78, 16], [82, 22],
    [86, 26], [80, 30], [76, 36], [70, 38], [50, 40], [44, 38], [30, 42], [86, 40],
  ];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
      {/* equator / longitude faint arcs */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.18]"
        viewBox="0 0 100 55"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7FA9D6" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#7FA9D6" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* horizontal latitude lines */}
        {[14, 22, 30, 38].map((y) => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="100"
            y2={y}
            stroke="#7FA9D6"
            strokeWidth="0.05"
            strokeDasharray="0.5 0.7"
          />
        ))}
        {/* longitude curves */}
        {[20, 40, 60, 80].map((x) => (
          <path
            key={x}
            d={`M ${x} 6 Q ${x + (x > 50 ? -3 : 3)} 27 ${x} 48`}
            stroke="#7FA9D6"
            strokeWidth="0.05"
            fill="none"
            strokeDasharray="0.5 0.7"
          />
        ))}
      </svg>
      {/* Animated territory dots */}
      <div className="absolute inset-0">
        {DOTS.map((pos, i) => {
          const [x, y] = pos;
          const delay = (i % 8) * 0.4 + (i % 3) * 0.2;
          return (
            <motion.span
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${x}%`,
                top: `${(y / 55) * 100}%`,
                width: 6,
                height: 6,
                background: i % 5 === 0 ? "#FFD400" : "#7FA9D6",
                boxShadow: `0 0 12px ${i % 5 === 0 ? "#FFD400" : "#7FA9D6"}`,
              }}
              animate={{
                opacity: [0.15, 0.95, 0.15],
                scale: [0.6, 1.15, 0.6],
              }}
              transition={{
                duration: 3.2,
                repeat: Infinity,
                ease: "easeInOut",
                delay,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

// Stream-with-eGeez tutorial: TV showing Netflix → eGeez QR scan → toy popup with Buy
function StreamWithEgeezTutorial({ netflix }) {
  const [step, setStep] = useState(0);
  const STEPS = ["Watch", "Scan", "Buy"];
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % 3), 1800);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="relative w-full max-w-[520px] mx-auto">
      {/* Step indicator OUTSIDE the wrapper that holds the phone — can never be covered */}
      <div
        className="mb-6 flex items-center justify-center gap-2 md:gap-3 bg-white/95 backdrop-blur-sm rounded-full mx-auto w-fit px-4 py-2 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.25)] relative z-30"
        data-testid="stream-step-indicator"
      >
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2 md:gap-3">
            <div className="flex items-center gap-1.5">
              <motion.span
                animate={{
                  background: i === step ? "#0041E6" : "#000",
                  scale: i === step ? 1.1 : 1,
                }}
                transition={{ duration: 0.35 }}
                className="inline-flex w-5 h-5 md:w-6 md:h-6 rounded-full text-white items-center justify-center text-[10px] md:text-[11px] font-black"
              >
                {i + 1}
              </motion.span>
              <motion.span
                animate={{ color: i === step ? "#0041E6" : "rgba(0,0,0,0.55)" }}
                transition={{ duration: 0.35 }}
                className="text-[11px] md:text-[13px] font-black tracking-[0.18em] uppercase"
              >
                {label}
              </motion.span>
            </div>
            {i < STEPS.length - 1 && (
              <span className="w-4 md:w-6 h-px bg-black/20" />
            )}
          </div>
        ))}
      </div>

      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative w-full"
      >
        <div className="absolute -inset-10 bg-[#FFD400]/25 blur-[80px] rounded-full pointer-events-none" aria-hidden />

        {/* TV frame */}
      <div className="relative bg-[#1A1A1F] rounded-[16px] p-2 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)]">
        <div className="rounded-[10px] overflow-hidden bg-black aspect-[16/10] relative">
          <img src={netflix} alt="Streaming platform" className="w-full h-full object-cover" draggable={false} />
          {/* eGeez logo scan target — pulsing */}
          <motion.div
            animate={{ scale: step === 0 ? [1, 1.12, 1] : step === 1 ? [1.12, 1.25, 1.12] : 1, opacity: step === 2 ? 0.5 : 1 }}
            transition={{ duration: 1.4, ease: "easeInOut", repeat: step === 2 ? 0 : Infinity }}
            className="absolute top-4 right-4 w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white flex items-center justify-center shadow-[0_10px_30px_rgba(255,212,0,0.55)] ring-2 ring-[#FFD400]"
          >
            <span
              className="font-display italic text-[#0041E6] font-black"
              style={{ fontFamily: "Inter", fontSize: 26, lineHeight: 1 }}
            >
              e
            </span>
          </motion.div>
          {/* Scanning beam — emphasized in step 1 */}
          <motion.div
            aria-hidden
            className="absolute top-4 right-4 w-12 h-12 md:w-14 md:h-14 rounded-2xl border-2 border-[#FFD400] pointer-events-none"
            animate={{ scale: step === 1 ? [1, 2.6, 2.6] : [1, 2, 2], opacity: step === 1 ? [0.95, 0, 0] : [0.4, 0, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
          />
        </div>
        {/* TV stand */}
        <div className="mx-auto h-2 w-24 bg-black rounded-b-md" />
      </div>

      {/* Floating PHONE — fixed height stage, content swaps by step */}
      <motion.div
        animate={{
          y: step === 1 ? -18 : 0,
          rotate: step === 1 ? -6 : 0,
          scale: step === 2 ? 1.04 : 1,
        }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="absolute bottom-2 -right-4 md:-right-8 w-[140px] md:w-[170px] bg-white rounded-[24px] shadow-[0_20px_40px_-12px_rgba(0,0,0,0.45)] border border-black/5 p-2.5 z-20 overflow-hidden"
        data-testid="stream-phone-mock"
      >
        {/* phone notch */}
        <div className="mx-auto h-1 w-10 bg-black/15 rounded-full mb-2" />
        {/* Step badge */}
        <div className="text-[8px] font-black tracking-[0.22em] uppercase text-[#0041E6] mb-2 text-center">
          Scan · Shop · Done
        </div>

        {/* Fixed-height stage where each step's visual fades in */}
        <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-[#F8F8FA]">
          {/* Step 0: QR code (Watch — ready to scan) */}
          <motion.div
            initial={false}
            animate={{ opacity: step === 0 ? 1 : 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0 flex items-center justify-center bg-white"
            aria-hidden={step !== 0}
          >
            <div className="grid grid-cols-5 gap-px w-[68%] h-[68%]">
              {Array.from({ length: 25 }).map((_, i) => (
                <div
                  key={i}
                  className={
                    [0, 4, 6, 8, 10, 12, 14, 18, 20, 22, 24].includes(i)
                      ? "bg-black"
                      : "bg-white"
                  }
                />
              ))}
            </div>
          </motion.div>

          {/* Step 1: Scanning beam over the QR */}
          <motion.div
            initial={false}
            animate={{ opacity: step === 1 ? 1 : 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0 bg-gradient-to-br from-[#0041E6]/10 to-[#FFD400]/30 flex items-center justify-center"
            aria-hidden={step !== 1}
          >
            <div className="grid grid-cols-5 gap-px w-[60%] h-[60%] opacity-80">
              {Array.from({ length: 25 }).map((_, i) => (
                <div
                  key={i}
                  className={
                    [0, 4, 6, 8, 10, 12, 14, 18, 20, 22, 24].includes(i)
                      ? "bg-black"
                      : "bg-white"
                  }
                />
              ))}
            </div>
            <motion.div
              animate={{ y: step === 1 ? ["-50%", "50%", "-50%"] : "-50%" }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute left-2 right-2 h-[3px] rounded-full bg-[#0041E6] shadow-[0_0_18px_4px_rgba(0,65,230,0.55)]"
              style={{ top: "50%" }}
              aria-hidden
            />
          </motion.div>

          {/* Step 2: Toy popup with Buy CTA */}
          <motion.div
            initial={false}
            animate={{
              opacity: step === 2 ? 1 : 0,
              scale: step === 2 ? 1 : 0.92,
            }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 bg-gradient-to-br from-[#FFD400]/35 via-white to-[#FF6B6B]/20 p-2 flex flex-col items-stretch justify-center"
            aria-hidden={step !== 2}
          >
            <div className="rounded-lg bg-gradient-to-br from-[#FFD400] to-[#FF6B6B] flex items-center justify-center flex-1 text-[36px]" aria-hidden>
              🧸
            </div>
            <div className="mt-1 text-[8px] font-black text-black leading-tight truncate">Limited Edition Toy</div>
          </motion.div>
        </div>

        {/* Bottom action bar — Buy button in step 2, "Tap to scan" in others */}
        <div className="relative mt-2 h-[22px]">
          <motion.div
            initial={false}
            animate={{ opacity: step === 2 ? 1 : 0, y: step === 2 ? 0 : 6 }}
            transition={{ duration: 0.35, delay: step === 2 ? 0.1 : 0 }}
            className="absolute inset-0 flex items-center"
            aria-hidden={step !== 2}
          >
            <button
              type="button"
              className="w-full text-[9px] font-black tracking-wider uppercase bg-[#0041E6] text-white rounded-full py-1 hover:bg-[#0033B8] transition"
              tabIndex={-1}
            >
              Buy · $24.99
            </button>
          </motion.div>
          <motion.div
            initial={false}
            animate={{ opacity: step !== 2 ? 1 : 0, y: step !== 2 ? 0 : -6 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 flex items-center justify-center text-[9px] font-bold tracking-wider uppercase text-black/55"
            aria-hidden={step === 2}
          >
            {step === 1 ? "Scanning…" : "Tap to scan"}
          </motion.div>
        </div>
      </motion.div>

      {/* Floating "Royalties live" pill */}
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-4 -left-4 z-20 flex items-center gap-1.5 bg-[#0041E6] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full shadow-xl"
      >
        <span className="relative flex w-1.5 h-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD400] opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FFD400]" />
        </span>
        Royalty Live
      </motion.div>
    </motion.div>
    </div>
  );
}

// Email-script CTA — dark premium closing section for licensors
function HelpLicenseesEmailCTA() {
  return (
    <section className="relative py-28 md:py-36 overflow-hidden bg-[#FAFAFA]" data-testid="help-licensees-email-cta">
      {/* Subtle spotlight cone from above */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 w-[140%] h-[60%] opacity-70"
          style={{
            background:
              "radial-gradient(ellipse 50% 100% at 50% 0%, rgba(255, 212, 0, 0.18) 0%, rgba(255, 212, 0, 0.05) 35%, transparent 70%)",
          }}
        />
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 w-[60%] h-[80%] opacity-60"
          style={{
            background:
              "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 212, 0, 0.06) 30%, transparent 80%)",
            clipPath: "polygon(40% 0%, 60% 0%, 80% 100%, 20% 100%)",
          }}
        />
      </div>

      <div className="mx-auto max-w-[820px] px-5 md:px-12 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="text-[10px] md:text-[11px] font-bold tracking-[0.4em] uppercase text-black/45 mb-10">
            For Licensors
          </div>
        </motion.div>

        {/* Director's chair illustration centred */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mb-10 relative"
          data-testid="director-chair-visual"
        >
          {/* Spotlight pool on the floor */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[280px] h-8 rounded-[50%] bg-[#FFD400]/15 blur-2xl" aria-hidden />
          <DirectorChair />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-black tracking-tight"
          style={{
            fontFamily:
              '"Inter", "Helvetica Neue", system-ui, sans-serif',
            fontSize: "clamp(28px, 3.6vw, 44px)",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            lineHeight: 1.15,
          }}
        >
          Help your licensees sell.<br />
          <span className="text-black/55 font-normal">Maximize royalties — without lifting a finger.</span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.18 }}
          className="mt-8 mx-auto max-w-[560px] space-y-5 text-[15px] md:text-[16px] text-black/65"
          style={{ lineHeight: 1.7 }}
        >
          <p>
            One email to your licensees is all it takes. We wrote it for you —
            ready to copy, paste, send.
          </p>
          <p>
            Our Onboarding Happiness Manager personally verifies and guides every
            licensee. So you never have to.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.26 }}
          className="mt-12"
        >
          <a
            href={T.unlockScript}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="email-script-cta"
            className="group inline-flex items-center gap-3 bg-black hover:bg-[#0041E6] text-white font-semibold text-[15px] md:text-[16px] rounded-full pl-7 pr-2 h-[54px] transition shadow-[0_14px_30px_-12px_rgba(0,0,0,0.45)]"
          >
            Get My Free Email Script
            <span className="w-[40px] h-[40px] rounded-full bg-white text-black flex items-center justify-center transition-transform group-hover:rotate-45">
              <ArrowUpRight size={18} />
            </span>
          </a>
          <p className="mt-5 text-[12px] md:text-[13px] text-black/45">
            No commitment. No sales call. Just the script.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// Licensors eInsights section — mirrors Licensees
function LicensorsEInsightsSection() {
  return (
    <section className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="einsights-section-licensors">
      <div className="mx-auto max-w-[1400px] px-5 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="lg:col-span-6">
            <div className="flex items-center gap-2 leading-none">
              <span className="font-black italic text-[#0041E6]" style={{ fontFamily: "Inter", fontSize: "clamp(80px, 11vw, 160px)", letterSpacing: "-0.04em" }}>e</span>
              <span className="font-black italic text-black" style={{ fontFamily: "Inter", fontSize: "clamp(60px, 8vw, 120px)", letterSpacing: "-0.02em" }}>Insights</span>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-5 inline-flex items-center gap-2 bg-black text-[#FFD400] text-[11px] font-black tracking-[0.3em] uppercase px-3 py-1.5 rounded-full shadow-lg">
              <motion.span animate={{ rotate: [0, 25, -25, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} className="inline-block">🛠️</motion.span>
              Coming Soon
              <motion.span animate={{ y: [0, -3, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.3 }} className="inline-block">🧰</motion.span>
            </motion.div>
            <h3 className="mt-8 font-display text-black leading-[0.95]" style={{ fontSize: "clamp(36px, 5vw, 64px)" }}>
              Source of Truth,<br />
              <span className="italic text-[#0041E6]">Finally!</span>
            </h3>
            <p className="mt-4 text-[16px] md:text-[18px] text-black/80 leading-relaxed max-w-xl">
              <strong>eInsights.</strong> Census-level data.
            </p>
            <p className="mt-3 text-[16px] md:text-[18px] text-black/70 leading-relaxed max-w-xl">
              AI market intelligence replacing guesswork with real data. Which licensee sold most. Which toy moved fastest. In which territory. To which fan. And why.
            </p>
            <div className="mt-6 flex items-center gap-3 text-[28px] md:text-[32px]">
              {["📊", "🤖", "📈", "🧠", "🎯"].map((e, i) => (
                <motion.span key={i} aria-hidden animate={{ y: [0, -6, 0], rotate: [-4, 4, -4] }} transition={{ duration: 3 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }} className="inline-block select-none">
                  {e}
                </motion.span>
              ))}
            </div>
            <div className="mt-8">
              <a href={T.letsTalk} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-semibold text-[16px] rounded-full pl-6 pr-2 h-[52px] transition shadow-[0_14px_30px_-12px_rgba(0,65,230,0.5)]">
                Get Early Access
                <span className="w-[40px] h-[40px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94, y: 20 }} whileInView={{ opacity: 1, scale: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-6 flex justify-center">
            <motion.div animate={{ y: [0, -10, 0], rotate: [-1.5, 1.5, -1.5] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} className="relative w-full max-w-[520px]">
              <div className="absolute -inset-10 bg-[#0041E6]/15 blur-[80px] rounded-full pointer-events-none" aria-hidden />
              <motion.span aria-hidden animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.1, 0.8] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="absolute -top-4 -right-4 z-20 text-[#FFD400]">
                <Sparkles size={32} fill="#FFD400" />
              </motion.span>
              <motion.span aria-hidden animate={{ opacity: [0.6, 1, 0.6], scale: [0.9, 1.2, 0.9] }} transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: 0.6 }} className="absolute -bottom-5 -left-5 z-20 text-[#0041E6]">
                <Sparkles size={24} fill="#0041E6" />
              </motion.span>
              <InsightsTablet />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */

export default function LicensorsPage() {
  return (
    <div data-testid="licensors-page" className="bg-[#FAFAFA]">
      {/* 1. HERO — Audit-Worry-Free, serious tone */}
      <section className="relative pt-12 md:pt-20 pb-24 overflow-hidden bg-white">
        {/* Subtle dark mesh background — no clouds */}
        <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden>
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #0041E6 1px, transparent 1px), linear-gradient(to bottom, #0041E6 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
          <div className="absolute -top-32 -left-40 w-[600px] h-[600px] rounded-full bg-[#FFD400]/12 blur-3xl" />
          <div className="absolute -bottom-32 -right-40 w-[500px] h-[500px] rounded-full bg-[#0041E6]/10 blur-3xl" />
        </div>
        <div className="mx-auto max-w-[1400px] px-5 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="mb-8">
            <div className="inline-flex items-center gap-2 text-[11px] md:text-[13px] font-black tracking-[0.32em] uppercase text-[#0041E6]">
              <span className="w-7 h-px bg-[#0041E6]" />
              <ShieldCheck size={14} strokeWidth={2.5} />
              Audit-Worry-Free. On eGeez.
              <span className="w-7 h-px bg-[#0041E6]" />
            </div>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.h1
                {...fadeUp}
                className="font-display text-black leading-[0.92]"
                style={{ fontSize: "clamp(40px, 6.4vw, 88px)" }}
              >
                Whatever sells on eGeez —{" "}
                <span className="italic text-[#0041E6]">you get paid for.</span>
              </motion.h1>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-6 font-display italic text-[20px] md:text-[24px] text-black/85 leading-snug max-w-2xl"
              >
                Exactly. Every unit. Every territory. Every time.<br />
                <span className="text-[#0041E6]">No audit required for eGeez sales.</span><br />
                No Excel. No manipulation. No surprises.
              </motion.p>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.18 }}
                className="mt-7 space-y-3 text-[15px] md:text-[16px] text-black/75 leading-relaxed max-w-2xl"
              >
                <p>
                  The world&apos;s first centralized licensing marketplace — spanning every category, every subcategory, every territory you authorize.
                </p>
                <p>
                  A marketplace built with your licensing agreements at its core. <strong className="text-black">Not bolted on. Not an afterthought.</strong>
                </p>
                <p>
                  Finally — a platform that enforces your terms, respects your territories, and has your back.
                </p>
              </motion.div>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <a
                  href={T.letsTalk}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-[#0041E6] hover:bg-[#0033B8] text-white font-bold text-[16px] md:text-[18px] rounded-full pl-7 pr-2 h-[56px] inline-flex items-center gap-3 shadow-[0_14px_30px_-12px_rgba(0,65,230,0.6)] transition"
                  data-testid="hero-lets-talk-btn"
                >
                  Let&apos;s Talk
                  <span className="w-[40px] h-[40px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                    <ArrowUpRight size={18} />
                  </span>
                </a>
                <a
                  href={T.unlockScript}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-black hover:bg-black hover:text-white text-black font-bold text-[16px] md:text-[18px] rounded-full px-6 h-[56px] inline-flex items-center justify-center transition"
                  data-testid="hero-unlock-script-btn"
                >
                  Tell My Licensees · Unlock Script
                </a>
                <a
                  href={RESOURCES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-resources-link"
                  className="text-black/70 hover:text-[#0041E6] underline-offset-4 hover:underline font-semibold text-[15px] inline-flex items-center gap-1 transition"
                >
                  Resources <ArrowUpRight size={14} />
                </a>
              </motion.div>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.32 }}
                className="mt-12 grid grid-cols-3 gap-x-6 gap-y-5 max-w-xl"
              >
                {[
                  { k: "Every Sale", v: "Traced" },
                  { k: "Every Territory", v: "Enforced" },
                  { k: "Every Royalty", v: "Yours" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-[#0041E6] font-display text-[20px] sm:text-[24px] md:text-[28px] leading-none">
                      {s.k}
                    </div>
                    <div className="text-[10px] sm:text-[11px] md:text-[12px] text-black/55 mt-1 uppercase tracking-wider font-semibold">
                      {s.v}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
            {/* RIGHT — Licensor Hero illustration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 flex justify-center"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-full max-w-[560px]"
              >
                <div className="absolute -inset-10 bg-[#FFD400]/30 blur-[80px] rounded-full pointer-events-none" aria-hidden />
                <img
                  src={A.heroLicensorImg}
                  alt="Licensee dashboard showing earnings, top sold products and the customer flow on eGeez"
                  className="relative w-full h-auto rounded-[20px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.25)] ring-1 ring-black/5"
                  draggable={false}
                  data-testid="licensor-hero-img"
                />
                {/* Floating "Royalties live" pill */}
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-4 -left-4 z-20 flex items-center gap-1.5 bg-[#0041E6] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full shadow-xl"
                >
                  <span className="relative flex w-1.5 h-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD400] opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FFD400]" />
                  </span>
                  Royalty Live
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. (No More MESS removed per user request) */}

      {/* (Press Marquee removed per user request) */}

      {/* 2.5. LICENSORS STILL LOSE BILLIONS — 3 silent killers */}
      <section className="relative py-20 md:py-28 bg-[#0A0A0A] text-white overflow-hidden" data-testid="silent-killers-section">
        <div className="absolute inset-0 -z-0 pointer-events-none" aria-hidden>
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, rgba(230, 57, 70, 0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255, 212, 0, 0.3) 0%, transparent 40%)",
            }}
          />
        </div>
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 relative">
          <motion.div {...fadeUp} className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-black tracking-[0.32em] uppercase text-[#E63946] mb-5">
              <AlertTriangle size={14} /> The Silent Killers
            </div>
            <h2
              className="font-display leading-[0.92]"
              style={{ fontSize: "clamp(40px, 6.5vw, 88px)" }}
            >
              Licensors still lose <span className="italic text-[#E63946]">billions.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-14">
            {[
              { e: "🔴", t: "Underreported royalties", b: "Paid on what they claim — not what they sold." },
              { e: "🔴", t: "Late payments", b: "Chasing money that was always yours." },
              { e: "🔴", t: "Territory violations", b: "Your IP sold where it was never authorized." },
            ].map((k, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7 hover:bg-white/8 transition-colors"
              >
                <div className="text-[28px] mb-4">{k.e}</div>
                <h3 className="font-display text-[22px] md:text-[26px] leading-tight">{k.t}</h3>
                <p className="mt-3 text-[14px] md:text-[15px] text-white/65 leading-relaxed">{k.b}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            className="text-center max-w-3xl mx-auto space-y-4 text-[15px] md:text-[17px] text-white/75 leading-relaxed"
          >
            <p>Excel sheets get manipulated. Audits cost millions. By the time you find the problem — the damage is done.</p>
            <p className="font-display italic text-[22px] md:text-[28px] text-white leading-snug">
              On eGeez? <span className="text-[#FFD400]">None of that.</span><br />
              Every sale traced. Every royalty yours. Every territory enforced.
            </p>
            <p className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD400] text-black text-[12px] md:text-[13px] font-black tracking-[0.22em] uppercase">
              <ShieldCheck size={14} /> No audit required
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2.6. CASE STUDIES — 3 real-world stories */}
      <section className="relative py-24 md:py-32 bg-white" data-testid="case-studies-section">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-black tracking-[0.32em] uppercase text-[#0041E6] mb-5">
              <FileText size={14} /> Case Studies · Real Numbers
            </div>
            <h2
              className="font-display text-black leading-[0.95] md:leading-[0.92] px-2"
              style={{ fontSize: "clamp(28px, 5.4vw, 76px)" }}
            >
              Three audits. <span className="italic text-[#0041E6]">Three reminders</span> the system is broken.
            </h2>
          </motion.div>

          <div className="space-y-12">
            {[
              {
                tag: "Case Study 01",
                amount: "$268,000",
                title: "The Channel Omission — Hidden in Plain Sight",
                body: [
                  "A royalty audit of a multinational billion-dollar licensee with sophisticated reporting systems revealed the company had been entering improper parameters when capturing sales data — quietly omitting entire channels of distribution and geographies from royalty calculations.",
                  "The licensor recovered $268,000 in legitimate royalties that were simply never reported.",
                ],
                punch: "A billion-dollar company. Sophisticated systems. Still manipulated.",
                close: "Not on eGeez. Every channel. Every SKU. Every sale — traced.",
                accent: "#0041E6",
              },
              {
                tag: "Case Study 02",
                amount: "Bankrupt.",
                title: "Yu-Gi-Oh & Pokémon — Hidden Income. Terminated License.",
                body: [
                  "4Kids Entertainment held US licensing rights to two of the biggest fan franchises in history — Pokémon and Yu-Gi-Oh. For years the royalties looked fine on paper. Then the Japanese licensors ran an audit.",
                  "Secret deals. Hidden income reclassified as service fees. Over 45% of home video revenue concealed from the rightful IP owners. Nearly $5 million in underpayments, wrongful deductions and unmet obligations uncovered in one audit.",
                  "The license was terminated. 4Kids filed for bankruptcy.",
                ],
                punch: "The revenue existed. The fans were buying. The royalties just never arrived.",
                close: "On eGeez — every transaction is attributed, categorized and traceable to your licensing agreement from day one. No reclassification. No hidden deals. No audit required.",
                accent: "#E63946",
              },
              {
                tag: "Case Study 03",
                amount: "$58 Million",
                title: "A Global Toy Giant — Five Years. One Contract Gap.",
                body: [
                  "One of the world's most recognised toy companies held a licensing agreement covering two iconic product lines. For five years, the inventor licensor believed royalties were being calculated too narrowly — excluding derivative products and visually similar lines entirely.",
                  "An arbitrator agreed. $72.9 million awarded. Settled for $58 million.",
                ],
                punch: "The sales were real. The products existed. The royalties just never came.",
                close: "On eGeez — every product is listed and attributed to your licensing agreement from day one. No interpretation gaps. No five-year disputes.",
                accent: "#FFD400",
              },
            ].map((c, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-[20px] sm:rounded-[28px] border border-black/8 bg-white shadow-[0_20px_40px_-20px_rgba(0,0,0,0.15)] overflow-hidden grid grid-cols-1 md:grid-cols-12"
              >
                <div
                  className="md:col-span-4 p-6 sm:p-8 md:p-10 text-white relative overflow-hidden"
                  style={{ background: c.accent === "#FFD400" ? "linear-gradient(135deg, #FFD400 0%, #F5B800 100%)" : c.accent === "#E63946" ? "linear-gradient(135deg, #1A1A1A 0%, #E63946 100%)" : "linear-gradient(135deg, #0041E6 0%, #001F7A 100%)" }}
                >
                  <div className={`text-[10px] font-black tracking-[0.28em] uppercase mb-4 ${c.accent === "#FFD400" ? "text-black/70" : "text-white/70"}`}>
                    {c.tag}
                  </div>
                  <div
                    className={`font-display leading-[0.9] break-words ${c.accent === "#FFD400" ? "text-black" : "text-white"}`}
                    style={{ fontSize: "clamp(28px, 4.5vw, 60px)" }}
                  >
                    {c.amount}
                  </div>
                  <div className={`mt-4 text-[11px] sm:text-[12px] font-bold tracking-wider uppercase ${c.accent === "#FFD400" ? "text-black/60" : "text-white/60"}`}>
                    Recovered after audit
                  </div>
                </div>
                <div className="md:col-span-8 p-6 sm:p-8 md:p-10">
                  <h3
                    className="font-display text-black leading-tight"
                    style={{ fontSize: "clamp(18px, 2.4vw, 32px)" }}
                  >
                    {c.title}
                  </h3>
                  <div className="mt-4 space-y-3 text-[14px] md:text-[15px] text-black/75 leading-relaxed">
                    {c.body.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                  <p className="mt-5 font-display italic text-[16px] sm:text-[18px] md:text-[20px] text-black leading-snug">
                    {c.punch}
                  </p>
                  <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#F0FDF4] border border-[#86EFAC] px-3 sm:px-4 py-3">
                    <Check size={16} className="text-[#15803D] flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <p className="text-[13px] md:text-[14px] text-[#0A4A1F] leading-snug">
                      {c.close}
                    </p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* 3. STREAM WITH eGeez — moved to end of page */}

      {/* 4. eINSIGHTS — moved further down (now sits just before Stream-with-eGeez) */}

      {/* 5. BENEFITS — recreated with 3 new texts */}
      <section className="relative py-24 md:py-32" style={{ background: "#FFD400" }} data-testid="benefits-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <motion.h2
            {...fadeUp}
            className="font-display text-black text-center leading-[0.9]"
            style={{ fontSize: "clamp(60px, 9vw, 140px)" }}
          >
            Benefits
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-center text-[16px] md:text-[18px] text-black/70 max-w-2xl mx-auto leading-relaxed"
          >
            Three things that change your relationship with licensing forever.
          </motion.p>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                emoji: "💰",
                tag: "Royalty Automation",
                title: "Get Paid. Automatically. Every Sale.",
                body: "Every Sale On eGeez — Every Royalty. Yours. Automatically. No Excel. No manual reports. No waiting. Every transaction on eGeez is traced, attributed to your licensing agreement, and generates your royalty automatically. Audit-free. Guaranteed.",
                bg: "#0041E6",
                fg: "#fff",
                accent: "#FFD400",
              },
              {
                emoji: "🗺️",
                tag: "Territorial Enforcement",
                title: "Your Territory. Enforced. Always.",
                body: "Your Territory. Enforced. Zero Violations. Your licensing agreement draws the boundary. eGeez enforces it at the transaction level. A fan outside your authorized territory simply cannot buy. No policing required. No violations possible. Compliance built into the infrastructure itself.",
                bg: "#0A0A0A",
                fg: "#fff",
                accent: "#FFD400",
              },
              {
                emoji: "🛡️",
                tag: "Pre-Verified Sellers",
                title: "Every Licensee. Verified. Before They Sell.",
                body: "Every Licensee Verified Before They Sell A Single Product. eSmart Gateway ensures no unauthorized seller ever reaches your fans on eGeez. Human and AI verification. Domain intelligence. Instant ePIN for verified licensees. Your IP protected before the first product goes live.",
                checkLine: "Every Seller. Verified. Before They Sell.",
                bg: "#5B2EBE",
                fg: "#fff",
                accent: "#FFD400",
              },
            ].map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                className="rounded-[32px] p-6 sm:p-8 md:p-10 relative overflow-hidden min-h-[460px] sm:min-h-[480px] flex flex-col"
                style={{ background: b.bg, color: b.fg }}
              >
                <div className="text-[40px] mb-3">{b.emoji}</div>
                <div
                  className="text-[10px] font-black tracking-[0.28em] uppercase mb-3"
                  style={{ color: b.accent }}
                >
                  {b.tag}
                </div>
                <h3
                  className="font-display italic text-[22px] sm:text-[26px] md:text-[32px] leading-[1.05] break-words [hyphens:auto]"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {b.title}
                </h3>
                <p className="mt-5 text-[14px] md:text-[15px] leading-relaxed opacity-90">
                  {b.body}
                </p>
                {b.checkLine && (
                  <div className="mt-auto pt-6 inline-flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#22C55E]">
                      <Check size={14} className="text-white" strokeWidth={3} />
                    </span>
                    <span className="text-[13px] font-bold" style={{ color: b.accent }}>
                      {b.checkLine}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. GEOFENCING / DISCOVERABILITY UNLOCKED — mirrors Licensees */}
      <section className="relative pb-24 pt-24 bg-[#FAFAFA]" data-testid="discoverability-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <motion.div {...fadeUp} className="rounded-[40px] overflow-hidden grid grid-cols-1 md:grid-cols-5 min-h-[520px]" style={{ background: "#1F3933" }}>
            <div className="relative md:col-span-3 bg-gradient-to-br from-[#FFB347] to-[#FF6B6B] flex items-center justify-center p-6 md:p-10 overflow-hidden min-h-[420px]">
              <motion.span aria-hidden animate={{ x: [-20, 20, -20], y: [0, -8, 0], rotate: [-10, 10, -10] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute top-6 left-6 text-4xl md:text-5xl select-none pointer-events-none z-20">👿</motion.span>
              <motion.span aria-hidden animate={{ x: [10, -15, 10], y: [-6, 8, -6], rotate: [8, -8, 8] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }} className="absolute top-14 right-10 text-3xl md:text-4xl select-none pointer-events-none z-20">😈</motion.span>
              <motion.span aria-hidden animate={{ y: [0, -14, 0], rotate: [-6, 6, -6] }} transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 1.2 }} className="absolute bottom-8 left-12 text-3xl select-none pointer-events-none z-20">🚫</motion.span>
              <motion.div animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-[#E63946] text-white text-[10px] font-black tracking-[0.24em] uppercase px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                <AlertTriangle size={12} strokeWidth={3} /> Territorial Breach Blocked
              </motion.div>
              <motion.div animate={{ y: [0, -4, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-5 right-5 z-20 bg-[#FFD400] text-black text-[10px] font-black tracking-[0.18em] uppercase px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
                ⚠️ Region-locked
              </motion.div>
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} className="relative z-[5] w-full max-w-[560px]">
                <div className="absolute -inset-6 bg-white/30 blur-3xl rounded-3xl" aria-hidden />
                <img src={A.cannotShip} alt="Geofenced checkout" className="relative w-full h-auto rounded-2xl shadow-[0_40px_80px_-20px_rgba(0,0,0,0.55)] ring-1 ring-black/10" draggable={false} />
              </motion.div>
            </div>
            <div className="relative md:col-span-2 p-10 md:p-12 text-white flex flex-col justify-center overflow-hidden">
              <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-black tracking-[0.28em] uppercase text-[#FFD400] mb-4">
                <span className="inline-block w-6 h-px bg-[#FFD400]" /> GeoShield · Region Aware
              </div>
              <h3 className="font-display italic text-[32px] md:text-[44px] leading-[0.95]">Geofencing Licensed Products</h3>
              <motion.div initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-3 font-display italic text-[24px] md:text-[32px] leading-[1] text-[#FFD400]">
                Discoverability Unlocked.
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.85, rotate: -10 }} whileInView={{ opacity: 1, scale: 1, rotate: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} className="mt-6 relative w-[90px] md:w-[110px] pointer-events-none select-none">
                <div className="absolute -inset-3 bg-[#FFD400]/30 blur-2xl rounded-full" aria-hidden />
                <motion.img src={A.geoShield} alt="eGeez GeoShield" animate={{ y: [0, -5, 0], rotate: [-3, 3, -3] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} className="relative w-full h-auto drop-shadow-[0_20px_40px_rgba(255,212,0,0.45)]" draggable={false} />
              </motion.div>
              <p className="mt-6 text-[14px] md:text-[15px] leading-relaxed text-white/90">
                By capturing exact licensed territories from your agreements, eGeez geolocates official licensed products so they only appear to fans in approved regions. Licensors safeguarded. Fans served localized, seamless shopping.
              </p>
              <div className="mt-8">
                <a href={T.letsTalk} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 bg-[#FFD400] hover:bg-white text-black font-bold text-[15px] rounded-full pl-5 pr-1.5 h-[46px] transition shadow-lg">
                  Activate GeoShield
                  <span className="w-[36px] h-[36px] rounded-full bg-black text-[#FFD400] flex items-center justify-center transition-transform group-hover:rotate-45">
                    <ArrowUpRight size={16} />
                  </span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6.5. PREMIERE DROP — for Licensors */}
      <section className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="premiere-drop-licensors">
        {[
          { x: "4%", y: "10%", s: 16, d: 0, dur: 4 },
          { x: "12%", y: "55%", s: 12, d: 0.6, dur: 5 },
          { x: "48%", y: "8%", s: 18, d: 0.3, dur: 4.8 },
          { x: "78%", y: "12%", s: 16, d: 0.5, dur: 4.6 },
          { x: "92%", y: "60%", s: 14, d: 1.4, dur: 4.2 },
        ].map((st, i) => (
          <motion.span
            key={i}
            aria-hidden
            style={{ left: st.x, top: st.y }}
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.15, 0.8], rotate: [0, 180, 360] }}
            transition={{ duration: st.dur, repeat: Infinity, ease: "easeInOut", delay: st.d }}
            className="absolute text-[#FFD400] pointer-events-none select-none"
          >
            <Sparkles size={st.s} fill="#FFD400" strokeWidth={1.5} />
          </motion.span>
        ))}

        <div className="mx-auto max-w-[1300px] px-5 md:px-12 relative">
          <motion.div {...fadeUp} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.28em] uppercase text-[#0041E6] mb-5">
              <span className="inline-block w-6 h-px bg-[#0041E6]" /> Pre-Order Window
            </div>
            <img src={A.premiereLogo} alt="Premiere Drop" className="h-14 md:h-20 w-auto mx-auto mb-6" draggable={false} />
            <h2 className="font-display text-black leading-[0.92] mx-auto" style={{ fontSize: "clamp(36px, 5.5vw, 76px)", maxWidth: "1000px" }}>
              Pre-Orders That <span className="italic text-[#0041E6]">Build Hype</span> — Before You Ship.
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="rounded-[40px] p-10 md:p-16 bg-white border border-black/8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.18)] grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5 text-[15px] md:text-[17px] text-black/80 leading-relaxed">
              <p>
                Premiere Drop opens a <strong className="text-black">licensor-approved pre-order window</strong> on eGeez — at prototype stage, before mass distribution. Like a movie premiering in theatres before streaming —
              </p>
              <p className="font-display italic text-[20px] md:text-[24px] text-[#0041E6] leading-snug">
                Your licensed product gets its moment first.
              </p>
              <p>Fans commit early. Hype builds organically. By the time product ships — demand is already proven.</p>
              <div className="pt-2">
                <div className="text-[11px] font-black tracking-[0.28em] uppercase text-black/60 mb-3">For licensors this means</div>
                <ul className="space-y-2.5">
                  {[
                    "Royalties flowing before shelves are even stocked",
                    "Real demand data on every approved product",
                    "Your window. Your approval. Your control.",
                    "Maximum fan excitement at launch",
                  ].map((line, j) => (
                    <li key={j} className="flex items-start gap-2 text-[14px] md:text-[15px]">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#22C55E] flex-shrink-0 mt-0.5">
                        <Check size={11} className="text-white" strokeWidth={3} />
                      </span>
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="pt-3 font-display italic text-[18px] md:text-[22px] text-black leading-snug">
                Your approval was always the green light.<br />
                <span className="text-[#0041E6]">Premiere Drop makes it the opening night.</span>
              </p>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} whileInView={{ opacity: 1, scale: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-5 flex justify-center">
              <div className="relative">
                <img src={A.premiereLeaderboard} alt="Premiere Drop leaderboard" className="w-full max-w-[420px] rounded-[28px] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)]" draggable={false} />
                <div className="absolute -top-4 -left-4 bg-black text-[#FFD400] text-[10px] font-black tracking-[0.25em] uppercase px-3 py-1.5 rounded-full rotate-[-6deg] shadow-lg">
                  ★ Leaderboard Live
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 7. eSMART GATEWAY + LICENSED PRODUCTS INTELLIGENCE */}
      <section className="relative py-24 md:py-32 bg-white" data-testid="esmart-section">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 space-y-10">
          <motion.div {...fadeUp} className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.32em] uppercase text-[#0041E6] mb-4">
              <Sparkles size={14} className="text-[#0041E6]" /> Proprietary Infrastructure
            </div>
          </motion.div>
          {/* Gateway card */}
          <motion.div {...fadeUp} className="rounded-[32px] p-10 md:p-14 bg-gradient-to-br from-[#0A0A0A] to-[#1A1A2E] text-white grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <h3 className="font-display leading-[0.95]" style={{ fontSize: "clamp(32px, 4.6vw, 56px)" }}>
                eSmart Gateway — <span className="italic text-[#FFD400]">We Already Know Who You Are.</span>
              </h3>
              <div className="mt-6 space-y-4 text-[15px] md:text-[16px] text-white/80 leading-relaxed">
                <p>The licensing industry runs on trust. But trust without verification is just paperwork.</p>
                <p>eGeez built something the industry never had — an intelligent, automated licensee verification system that doesn&apos;t wait for you to prove yourself. <strong className="text-white">It already knows.</strong></p>
                <p>Our eSmart Gateway combines human expertise and proprietary AI to authenticate every licensee before they touch the platform. Domain verification. Automated licensee database checks. Email-triggered workflows. Rigorous due diligence — completed in seconds.</p>
                <p><strong className="text-[#FFD400]">Verified licensees receive their unique ePIN instantly.</strong> New to eGeez? Same-day verification. Human and AI working together.</p>
                <p className="font-display italic text-[18px] md:text-[20px] text-white">Sometimes eGeez already knows who you are before you sign up. That&apos;s not coincidence. That&apos;s years of work.</p>
              </div>
            </div>
            <div className="lg:col-span-5 flex justify-center">
              <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} className="relative w-full max-w-[320px] rounded-2xl bg-white/5 border border-white/15 backdrop-blur-sm p-6">
                <div className="text-[10px] font-black tracking-[0.26em] uppercase text-[#FFD400] mb-4">Verification Status</div>
                {[
                  { l: "Domain check", s: "Passed" },
                  { l: "Database match", s: "Passed" },
                  { l: "Human review", s: "Passed" },
                  { l: "ePIN issued", s: "Live" },
                ].map((row, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.15 }}
                    className="flex items-center justify-between py-2.5 border-b border-white/8 last:border-0"
                  >
                    <span className="text-[13px] text-white/85">{row.l}</span>
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-wider uppercase text-[#22C55E]">
                      <Check size={12} strokeWidth={3} /> {row.s}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* Products Intelligence card — NASA mission control / live AI dataset */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="relative rounded-[32px] overflow-hidden text-white"
            style={{
              background:
                "radial-gradient(120% 80% at 50% 0%, #0F1B3A 0%, #07101F 60%, #050B16 100%)",
            }}
            data-testid="licensed-products-intelligence-card"
          >
            {/* Animated world map dots — territories lighting up */}
            <IntelligenceWorldMap />

            {/* faint grid overlay */}
            <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              aria-hidden
              style={{
                backgroundImage:
                  "linear-gradient(to right, #7FA9D6 1px, transparent 1px), linear-gradient(to bottom, #7FA9D6 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

            {/* Top status bar */}
            <div className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-6 md:pt-8 text-[10px] md:text-[11px] tracking-[0.32em] uppercase">
              <div className="flex items-center gap-2 text-[#7FA9D6]/80">
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity }}
                />
                eSmart · Live
              </div>
              <div className="hidden md:flex items-center gap-2 text-[#7FA9D6]/60">
                <Sparkles size={12} /> Indexing globally
              </div>
              <div className="flex items-center gap-2">
                <img
                  src={LOGO_SRC}
                  alt="eGeez"
                  className="h-5 md:h-6 w-auto rounded-md bg-[#FFD400] px-1.5 py-0.5 shadow-[0_8px_22px_-6px_rgba(255,212,0,0.55)]"
                  data-testid="esmart-egeez-logo"
                />
              </div>
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 px-6 md:px-10 py-12 md:py-16">
              <div className="lg:col-span-7">
                <h3
                  className="text-white tracking-tight"
                  style={{
                    fontFamily:
                      '"Inter", "Helvetica Neue", system-ui, sans-serif',
                    fontWeight: 700,
                    letterSpacing: "-0.015em",
                    fontSize: "clamp(34px, 5vw, 64px)",
                    lineHeight: 1.04,
                  }}
                >
                  The Heavy Lifting<br />Nobody Else Did.
                </h3>
                <div
                  className="mt-7 space-y-4 text-[15px] md:text-[16px] text-white/70"
                  style={{ fontFamily: '"Inter", system-ui, sans-serif', lineHeight: 1.75 }}
                >
                  <p>
                    While others published surveys —{" "}
                    <strong className="text-white">eGeez built a dataset.</strong>
                  </p>
                  <p>
                    Our proprietary eSmart AI has automatically indexed tens of thousands of
                    licensed consumer products globally — catalogued by SKU, by licensee, by
                    territory, by category.
                  </p>
                  <p>
                    The world&apos;s only Licensed Consumer Products Intelligence system of its kind.
                    Every licensee domain analysed. Every product traced. Every territory mapped.
                  </p>
                </div>
              </div>

              {/* 4 glowing data cards with animated counters */}
              <div className="lg:col-span-5 grid grid-cols-2 gap-3 md:gap-4 self-center">
                {[
                  { v: 100000, suffix: "+", l: "SKUs Indexed", color: "#FFD400" },
                  { v: 100, suffix: "%", l: "Domains Analysed", color: "#7FA9D6" },
                  { v: "Global", l: "Territory Mapped", color: "#22C55E" },
                  { v: 1, suffix: "-Click", l: "Claim Flow", color: "#FF6B6B" },
                ].map((s, i) => (
                  <DataCard key={i} card={s} delay={i * 0.12} />
                ))}
              </div>
            </div>

            {/* Hero closing line — isolated revelation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 px-6 md:px-10 pb-14 md:pb-20 text-center"
              data-testid="intelligence-revelation"
            >
              <div className="mx-auto max-w-[820px] border-t border-white/8 pt-12 md:pt-14">
                <div
                  className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-[#FFD400]/85 mb-4"
                  style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 700 }}
                >
                  System Match Detected
                </div>
                <p
                  className="text-white"
                  style={{
                    fontFamily:
                      '"Inter", "Helvetica Neue", system-ui, sans-serif',
                    fontSize: "clamp(28px, 3.8vw, 48px)",
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.15,
                  }}
                >
                  Your licensed products are likely{" "}
                  <span className="text-[#FFD400]">already in our system.</span>
                </p>
                <p className="mt-4 text-[14px] md:text-[15px] text-white/55 max-w-xl mx-auto">
                  We just need you to claim them.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 8. RESEARCH PAPER CARD — prestigious academic redesign */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{ background: "#1A1814" }}
        data-testid="research-paper-section"
      >
        {/* Subtle paper texture + gold radial accents */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(212, 175, 55, 0.18) 0px, rgba(212, 175, 55, 0.18) 1px, transparent 1px, transparent 6px)",
            }}
          />
          <div
            className="absolute -top-32 left-1/2 -translate-x-1/2 w-[80%] h-[400px] opacity-50"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(212, 175, 55, 0.18) 0%, transparent 65%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(212,175,55,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(212,175,55,0.25) 1px, transparent 1px)",
              backgroundSize: "120px 120px",
            }}
          />
        </div>

        <div className="mx-auto max-w-[1100px] px-5 md:px-12 relative">
          {/* Eyebrow with gold dividers */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-3 mb-10 text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-[#D4AF37]"
            style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 600 }}
          >
            <span className="w-12 h-px bg-[#D4AF37]/50" />
            Peer-Reviewed Publication
            <span className="w-12 h-px bg-[#D4AF37]/50" />
          </motion.div>

          {/* Official seal — circular gold stamp */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
            whileInView={{ opacity: 1, scale: 1, rotate: -4 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mb-10 relative"
            style={{ width: 150, height: 150 }}
            data-testid="les-nouvelles-seal"
          >
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full">
              <defs>
                <path
                  id="sealCircle"
                  d="M 100,100 m -78,0 a 78,78 0 1,1 156,0 a 78,78 0 1,1 -156,0"
                />
              </defs>
              <circle
                cx="100"
                cy="100"
                r="92"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="1"
                opacity="0.5"
              />
              <circle
                cx="100"
                cy="100"
                r="78"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="2"
              />
              <circle
                cx="100"
                cy="100"
                r="62"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="0.6"
                opacity="0.6"
              />
              <text
                fill="#D4AF37"
                fontSize="11"
                fontFamily="Inter, sans-serif"
                fontWeight="700"
                letterSpacing="3"
              >
                <textPath href="#sealCircle" startOffset="0">
                  · LES NOUVELLES · JUNE 2026 · PUBLISHED ·
                </textPath>
              </text>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <ScrollText size={24} className="text-[#D4AF37] mb-1.5" strokeWidth={1.4} />
              <div
                className="text-[#D4AF37] text-[16px]"
                style={{
                  fontFamily: '"Inter", system-ui, sans-serif',
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                }}
              >
                LES
              </div>
              <div
                className="text-[#D4AF37]/80 text-[8px] tracking-[0.2em]"
                style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 600 }}
              >
                EST. 1965
              </div>
            </div>
          </motion.div>

          {/* Headline — clean single font */}
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-center text-white mx-auto max-w-4xl"
            style={{
              fontFamily: '"Inter", "Helvetica Neue", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: "clamp(28px, 4.2vw, 56px)",
              letterSpacing: "-0.015em",
              lineHeight: 1.12,
            }}
          >
            The Standardized Licensing Verification Layer.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="text-center mt-4 mx-auto max-w-3xl text-white/60"
            style={{
              fontFamily: '"Inter", system-ui, sans-serif',
              fontSize: "clamp(15px, 1.6vw, 19px)",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            An AI-driven infrastructure for global IP — published in the peer-reviewed
            journal of the Licensing Executives Society.
          </motion.p>

          {/* Author + abstract */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.26 }}
            className="mt-12 mx-auto max-w-3xl"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3 sm:gap-6 text-center">
              <div
                className="text-[10px] tracking-[0.3em] uppercase text-white/40"
                style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 600 }}
              >
                Author
              </div>
              <div
                className="text-[#D4AF37]"
                style={{
                  fontFamily: '"Inter", system-ui, sans-serif',
                  fontSize: "20px",
                  fontWeight: 700,
                  letterSpacing: "0.01em",
                }}
              >
                Nathen Mazri
              </div>
              <div
                className="text-[10px] tracking-[0.3em] uppercase text-white/40"
                style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 600 }}
              >
                Founder · LES Member
              </div>
            </div>

            <div
              className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 text-white/65"
              style={{
                fontFamily: '"Inter", system-ui, sans-serif',
                fontSize: "15px",
                lineHeight: 1.75,
                fontWeight: 400,
              }}
            >
              <p>
                We are solving a fundamental infrastructure problem in global IP licensing:
                there is no standardized, machine-readable system to verify licensing rights,
                licensed product legitimacy, and royalty compliance across borders.
              </p>
              <p>
                Our R&amp;D is focused on building an AI-driven verification layer and
                standardized data model that can operate across fragmented global datasets —
                passing full editorial board review with excellent grading.
              </p>
            </div>
          </motion.div>

          {/* Hero closing line */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-20 text-center"
          >
            <div className="mx-auto w-12 h-px bg-[#D4AF37]/60 mb-8" aria-hidden />
            <p
              className="mx-auto max-w-3xl text-white"
              style={{
                fontFamily: '"Inter", "Helvetica Neue", system-ui, sans-serif',
                fontSize: "clamp(28px, 3.6vw, 44px)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              That&rsquo;s a <span className="text-[#D4AF37]">standard being set.</span>
            </p>
          </motion.div>

          {/* Premium CTA */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-12 flex justify-center"
          >
            <a
              href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5914684#"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="research-paper-link"
              className="group inline-flex items-center gap-3 rounded-full pl-7 pr-2 h-[56px] transition shadow-[0_18px_40px_-12px_rgba(212,175,55,0.45)]"
              style={{
                background:
                  "linear-gradient(135deg, #D4AF37 0%, #B8911E 100%)",
                color: "#1A1814",
                fontFamily: '"Inter", system-ui, sans-serif',
                fontWeight: 700,
                fontSize: "16px",
                letterSpacing: "0.01em",
              }}
            >
              Read on SSRN
              <span className="w-[40px] h-[40px] rounded-full bg-[#1A1814] text-[#D4AF37] flex items-center justify-center transition-transform group-hover:rotate-45">
                <ArrowUpRight size={18} />
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* 9. ONBOARD YOUR OWN MERCHANDISE — coral, button → Sign-Up */}
      <section className="relative py-20 md:py-28" style={{ background: "#FF6B6B" }}>
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp} className="flex justify-center">
              <ProductListTablet />
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
              <div className="inline-flex items-center gap-2 bg-[#FFD400] text-black px-3 py-1 rounded-full font-black text-[11px] tracking-[0.2em] mb-4">
                <Star size={12} className="fill-black" />
                FROM THE STUDIO
              </div>
              <h2
                className="font-display italic text-black leading-[0.95]"
                style={{ fontSize: "clamp(40px, 5.4vw, 72px)" }}
              >
                Have your own merch<br />to sell directly?
              </h2>
              <p className="mt-6 text-[16px] md:text-[18px] text-white leading-relaxed max-w-xl">
                Use the eLicensee portal to list and sell your studio-owned products on eGeez — no middleman, ships directly from you to fans in your licensed territories.
              </p>
              <a
                href={T.signUp}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 group inline-flex items-center gap-3 bg-black hover:bg-[#0041E6] text-white font-bold text-[16px] rounded-full pl-7 pr-2 h-[54px] transition shadow-lg"
                data-testid="merch-sign-up-btn"
              >
                Sign-Up
                <span className="w-[40px] h-[40px] rounded-full bg-[#FFD400] text-black flex items-center justify-center transition-transform group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 11. HELP LICENSEES SELL — Email Script CTA, dark premium */}
      <HelpLicenseesEmailCTA />

      {/* 11.5. FIGHTING COUNTERFEITS — clean cybersecurity statement */}
      <section
        className="relative py-28 md:py-36 overflow-hidden"
        style={{ background: "#0B1220" }}
        data-testid="fighting-counterfeits-section"
      >
        {/* Subtle cyber-grid background */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #93B4D6 1px, transparent 1px), linear-gradient(to bottom, #93B4D6 1px, transparent 1px)",
              backgroundSize: "56px 56px",
            }}
          />
          {/* slow horizontal scan line */}
          <motion.div
            className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#7FA9D6]/40 to-transparent"
            animate={{ top: ["0%", "100%"] }}
            transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          />
        </div>

        <div className="mx-auto max-w-[1100px] px-5 md:px-12 relative">
          {/* Eyebrow with subtle steel divider */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-3 mb-12 text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-[#7FA9D6]"
            style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 600 }}
          >
            <span className="w-10 h-px bg-[#7FA9D6]/40" />
            Fighting Counterfeits
            <span className="w-10 h-px bg-[#7FA9D6]/40" />
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Shield + barcode visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 flex justify-center order-2 lg:order-1"
            >
              <div className="relative w-[280px] md:w-[340px] aspect-square">
                {/* Outer rotating ring */}
                <motion.div
                  className="absolute inset-0 rounded-full border border-[#7FA9D6]/20"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                  aria-hidden
                />
                <motion.div
                  className="absolute inset-6 rounded-full border border-[#7FA9D6]/15"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
                  aria-hidden
                />
                {/* Vertical scan line over shield */}
                <motion.div
                  className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#7FA9D6]/70 to-transparent pointer-events-none"
                  animate={{ x: ["-50%", "50%", "-50%"], opacity: [0.3, 0.9, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  style={{ left: "50%" }}
                  aria-hidden
                />
                {/* eGeez logo (white, transparent) over the shield */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Shield
                    size={180}
                    strokeWidth={1.1}
                    className="text-white/85"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <img
                    src={LOGO_SRC}
                    alt="eGeez"
                    className="w-[38%] h-auto select-none"
                    style={{ filter: "brightness(0) invert(1)" }}
                    draggable={false}
                    data-testid="counterfeits-egeez-logo"
                  />
                </div>
                {/* Barcode strip beneath */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-end gap-[2px] h-6 opacity-70">
                  {[3,5,2,4,3,5,4,2,5,3,4,5,2,4].map((h, i) => (
                    <span
                      key={i}
                      className="w-[2px] bg-[#7FA9D6]"
                      style={{ height: `${h * 4}px` }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Headline + body */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-7 order-1 lg:order-2"
            >
              <h2
                className="text-white tracking-tight"
                style={{
                  fontFamily:
                    '"Inter", "Helvetica Neue", system-ui, sans-serif',
                  fontWeight: 700,
                  letterSpacing: "-0.015em",
                  fontSize: "clamp(34px, 5vw, 64px)",
                  lineHeight: 1.05,
                }}
              >
                The best weapon against counterfeits isn&rsquo;t a lawsuit.
              </h2>
              <p
                className="mt-5 text-[18px] md:text-[20px] text-white/55"
                style={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 400 }}
              >
                It&rsquo;s making the real thing impossible to miss.
              </p>

              <div
                className="mt-10 space-y-5 text-[15px] md:text-[16px] text-white/70 max-w-xl"
                style={{ fontFamily: '"Inter", system-ui, sans-serif', lineHeight: 1.75 }}
              >
                <p>
                  Counterfeits don&rsquo;t win because fans want fakes. They win because fans can&rsquo;t find the real thing.
                </p>
                <p className="text-white/45">
                  Wrong territory. No local distributor. Checkout blocked.
                </p>
                <p>
                  eGeez removes every excuse to go elsewhere. Every verified licensee. Every authorized product. Every licensed territory — discoverable, available, legally yours to buy.
                </p>
              </div>

              {/* Stat — calm, not loud */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-12 flex items-baseline gap-4"
              >
                <div
                  className="text-white"
                  style={{
                    fontFamily: '"Inter", system-ui, sans-serif',
                    fontSize: "clamp(36px, 4.4vw, 56px)",
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                  }}
                >
                  $3T
                </div>
                <p
                  className="text-[12px] md:text-[13px] text-white/50 max-w-[260px]"
                  style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
                >
                  estimated annual cost of counterfeit goods to the global licensing industry. Enforcement was never the answer — accessibility is.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LinkedIn follow + Final CTA */}
      <section className="relative py-16 bg-white" data-testid="linkedin-section">
        <div className="mx-auto max-w-[900px] px-5 md:px-12 text-center">
          <div className="text-[10px] font-black tracking-[0.32em] uppercase text-black/40 mb-4">
            Stay in the loop
          </div>
          <p className="font-display text-black leading-tight mb-6" style={{ fontSize: "clamp(22px, 3vw, 36px)" }}>
            Follow eGeez on LinkedIn for licensing intelligence, news &amp; product updates.
          </p>
          <a
            href="https://www.linkedin.com/company/egeez/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="linkedin-follow-btn"
            className="group inline-flex items-center gap-3 bg-[#0A66C2] hover:bg-[#0855A0] text-white font-bold text-[16px] rounded-full pl-7 pr-2 h-[54px] transition shadow-[0_14px_30px_-12px_rgba(10,102,194,0.55)]"
          >
            <Linkedin size={20} fill="white" strokeWidth={0} />
            Follow eGeez on LinkedIn
            <span className="w-[40px] h-[40px] rounded-full bg-white text-[#0A66C2] flex items-center justify-center transition-transform group-hover:rotate-45">
              <ArrowUpRight size={18} />
            </span>
          </a>
        </div>
      </section>

      {/* (removed: duplicate sections cleaned up) */}
      {/* (Final CTA moved to the very end of the page — see below) */}

      {/* 13. eINSIGHTS — Source of Truth, mirrors Licensees (moved here) */}
      <LicensorsEInsightsSection />

      {/* 14. STREAM WITH eGeez — partner pitch */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: "#FFD400" }}
        data-testid="stream-with-egeez-section"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12 items-center">
            <motion.div {...fadeUp} className="flex justify-center order-2 lg:order-1">
              <StreamWithEgeezTutorial netflix={A.netflix} />
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="order-1 lg:order-2"
            >
              <Eyebrow color="#0041E6">Streaming Partners</Eyebrow>
              <h2
                className="font-display italic text-black leading-[0.95] mt-4"
                style={{ fontSize: "clamp(48px, 7vw, 96px)" }}
              >
                Stream with<br />eGeez
              </h2>
              <p className="mt-6 text-[17px] md:text-[19px] text-black/85 leading-relaxed max-w-xl">
                Own a streaming platform? Partner with eGeez and let viewers scan a QR code while
                watching to instantly browse and shop officially licensed merchandise.
                One scan. One tap. The toy on screen — in their hands.
              </p>
              <ul className="mt-6 space-y-2.5 text-[15px] md:text-[16px] text-black/85">
                {[
                  "Frame-accurate product matching to the scene",
                  "Region-aware checkout — only what ships to your viewer",
                  "Royalties traced to the exact moment a fan converted",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-1 inline-flex w-5 h-5 rounded-full bg-[#0041E6] text-white items-center justify-center flex-shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <a
                href={T.letsTalk}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-bold text-[16px] rounded-full pl-7 pr-2 h-[54px] transition shadow-[0_14px_30px_-12px_rgba(0,65,230,0.55)]"
                data-testid="stream-lets-talk-btn"
              >
                Let's Talk
                <span className="w-[40px] h-[40px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 15. FINAL CTA — moved to very end, just before footer */}
      <section className="relative py-24 md:py-32 bg-[#FAFAFA]" data-testid="final-cta-section">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12">
          <motion.div
            {...fadeUp}
            className="relative rounded-[40px] overflow-hidden p-12 md:p-20 text-white text-center"
            style={{
              backgroundImage: `url(${A.bgBlue})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundColor: "#0041E6",
            }}
          >
            <div className="absolute inset-0 bg-[#0041E6]/30" />
            <div className="relative z-10">
              <Eyebrow color="#FFD400">Ready to protect your IP</Eyebrow>
              <h2
                className="mt-5 font-display text-white leading-[0.95]"
                style={{ fontSize: "clamp(44px, 7vw, 90px)" }}
              >
                Let&apos;s build the future<br />of licensing.
              </h2>
              <p className="mt-6 text-[18px] md:text-[20px] text-white/90 max-w-xl mx-auto leading-snug">
                Talk to our team about geofencing your territories, onboarding licensees, and unlocking real-time royalty data.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <a
                  href={T.letsTalk}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white text-[#0041E6] font-bold text-[18px] rounded-full pl-8 pr-2 h-[60px] hover:bg-[#FFD400] transition shadow-2xl"
                  data-testid="final-lets-talk-btn"
                >
                  Let&apos;s Talk
                  <span className="w-[44px] h-[44px] rounded-full bg-[#0041E6] text-white flex items-center justify-center">
                    <ArrowUpRight size={20} />
                  </span>
                </a>
                <a
                  href={T.unlockScript}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 border-2 border-white text-white font-bold text-[18px] rounded-full px-8 h-[60px] hover:bg-white hover:text-[#0041E6] transition"
                >
                  Unlock Script
                </a>
                <a
                  href="https://help.egeez.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-resources-btn"
                  className="inline-flex items-center gap-2 text-white/85 hover:text-[#FFD400] underline-offset-4 hover:underline font-bold text-[15px] transition"
                >
                  Resources <ArrowUpRight size={16} />
                </a>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-white/70 text-[13px]">
                <Check size={14} /> Verified licensees · <Check size={14} /> Geofenced territories ·{" "}
                <Check size={14} /> Real-time royalties
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
